// =============================================================================
// Surface helpers shared by the Admin/Player renderers and tests.
//
//   - Bézier surface evaluation (NBL 78 — 베지어 서피스 워핑)
//   - Point-in-polygon test for masks (NBL 46 — 캘리 마스킹)
//
// Kept framework-free (pure functions over plain {x,y}) so the GLSL renderers,
// the React canvas overlays, and vitest can all share one definition.
// =============================================================================

import type { BezierPatch, MaskPolygon, Point, WallSide } from './types.js';

/** Cubic Bernstein basis for parameter t∈[0,1] → [b0,b1,b2,b3]. */
export function bernstein3(t: number): [number, number, number, number] {
  const u = 1 - t;
  return [u * u * u, 3 * u * u * t, 3 * u * t * t, t * t * t];
}

/**
 * The identity 4×4 control grid: evenly spaced points covering the unit square
 * (row-major, row 0 = top). Evaluating a patch with this grid returns (u,v)
 * verbatim, i.e. zero displacement — the no-op baseline.
 */
export function identityControlPoints(): Point[] {
  const pts: Point[] = [];
  for (let row = 0; row < 4; row++) {
    for (let col = 0; col < 4; col++) {
      pts.push({ x: col / 3, y: row / 3 });
    }
  }
  return pts;
}

/** True when the patch's control grid is (within eps) the identity grid. */
export function isIdentityPatch(patch: BezierPatch, eps = 1e-6): boolean {
  if (patch.controlPoints.length !== 16) return false;
  const id = identityControlPoints();
  return patch.controlPoints.every(
    (p, i) => Math.abs(p.x - id[i]!.x) < eps && Math.abs(p.y - id[i]!.y) < eps,
  );
}

/**
 * Evaluate a bicubic Bézier surface at (u,v)∈[0,1]² where u runs left→right
 * (column) and v runs top→bottom (row). `controlPoints` is row-major 4×4.
 * Returns the displaced destination point.
 */
export function evalBezierPatch(
  controlPoints: Point[],
  u: number,
  v: number,
): Point {
  const bu = bernstein3(u);
  const bv = bernstein3(v);
  let x = 0;
  let y = 0;
  for (let row = 0; row < 4; row++) {
    for (let col = 0; col < 4; col++) {
      const w = bv[row]! * bu[col]!;
      const cp = controlPoints[row * 4 + col]!;
      x += w * cp.x;
      y += w * cp.y;
    }
  }
  return { x, y };
}

/**
 * Total destination displacement at a destination-global point from every
 * patch on `side`. For each patch we treat the patch's bounding parameter
 * domain as the unit square in destination space: we read the param (u,v) of
 * the query point relative to the identity grid (i.e. u=x, v=y), evaluate the
 * surface, and return the delta from the identity result. Identity patches
 * contribute zero. The sum is added to the source-UV in the renderer.
 */
export function bezierDisplacement(
  patches: BezierPatch[],
  side: WallSide,
  dest: Point,
): Point {
  let dx = 0;
  let dy = 0;
  for (const patch of patches) {
    if (patch.side !== side) continue;
    if (patch.controlPoints.length !== 16) continue;
    const u = Math.min(1, Math.max(0, dest.x));
    const v = Math.min(1, Math.max(0, dest.y));
    const warped = evalBezierPatch(patch.controlPoints, u, v);
    dx += warped.x - u;
    dy += warped.y - v;
  }
  return { x: dx, y: dy };
}

/**
 * Even-odd point-in-polygon test in [0,1] destination space.
 * `points` is the closed polygon (implicitly closed; last→first edge added).
 */
export function pointInPolygon(points: Point[], p: Point): boolean {
  let inside = false;
  const n = points.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const a = points[i]!;
    const b = points[j]!;
    const intersect =
      a.y > p.y !== b.y > p.y &&
      p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x;
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * True when destination point `p` is masked OUT (should render black) given
 * the polygon mask set. A non-inverted mask blacks out its interior; an
 * inverted mask blacks out everything outside it. Multiple masks union.
 */
export function isMasked(masks: MaskPolygon[], p: Point): boolean {
  for (const m of masks) {
    if (m.points.length < 3) continue;
    const inside = pointInPolygon(m.points, p);
    if (m.invert ? !inside : inside) return true;
  }
  return false;
}
